import { useMemo } from 'react'
import { Card, Chip, EmptyState, Table } from '@heroui/react'
import { Pager, usePaged } from '../ui/Pager'
import { useStaffText } from '../ui/StaffText'
import type { HBar } from '../ui/LiveCharts'
import { sortBranches, type FloorOverview } from '../lib/floorOverview'
import { PAGE_SIZES } from '../lib/paging'
import { dashSeries, needsAttention, overviewDesks, seriesColour, type FloorSeries } from '../lib/liveDash'
import { ChartRow, KpiRow, SectionTitle, StatusChip, TrendRow, attentionItems, minWord, useTimes } from './FloorParts'

/** Every branch at once: the network's figures and charts, then a branch per row. */
export function AllBranches({
  overview,
  series,
  onChoose,
}: {
  overview: FloorOverview
  series: FloorSeries | null
  onChoose: (branchId: string) => void
}) {
  const { say } = useStaffText()
  const t = overview.totals
  // Name order gives each branch its colour, the same in the bars and the table.
  const named = useMemo(() => sortBranches(overview.branches, 'name'), [overview.branches])
  const colour = (id: string) => seriesColour(named.findIndex((b) => b.branchId === id))
  const d = dashSeries(series, { waiting: t.waiting, avgWaitSec: t.avgWaitSec, longestWaitSec: t.longestWaitSec })
  const times = useTimes(d.ends)
  const paged = usePaged(named, 'name')

  const attention = attentionItems(
    needsAttention({
      closedBranches: named.filter((b) => b.waiting > 0 && b.desksOpen === 0).map((b) => ({ name: b.name, waiting: b.waiting })),
      over: t.overTarget,
      near: t.nearTarget,
      long: { n: t.longServing, who: named.filter((b) => b.longServing > 0).map((b) => b.name) },
    }),
    say,
  )
  if (t.requests > 0) {
    attention.push({
      key: 'requests',
      level: 'warning',
      icon: 'pause',
      title: say('hall.requests.title'),
      sub: named
        .filter((b) => b.requests > 0)
        .map((b) => `${b.name} · ${b.requests}`)
        .join(', '),
    })
  }

  // The busiest branches, at most eight, in their own colours.
  const byRows: HBar[] = named
    .filter((b) => b.waiting > 0)
    .sort((a, b) => b.waiting - a.waiting || a.name.localeCompare(b.name))
    .slice(0, 8)
    .map((b) => ({ key: b.branchId, label: b.name, value: b.waiting, shown: String(b.waiting), colour: colour(b.branchId), late: b.overTarget > 0 }))
  const vsRows: HBar[] = named
    .filter((b) => b.waiting > 0)
    .sort((a, b) => b.avgWaitSec - a.avgWaitSec)
    .slice(0, 8)
    .map((b) => ({ key: b.branchId, label: b.name, value: b.avgWaitSec, shown: minWord(b.avgWaitSec, say), colour: colour(b.branchId), late: b.overTarget > 0 }))

  return (
    <>
      <KpiRow
        waiting={t.waiting}
        served={t.served}
        avgWaitSec={t.waiting ? t.avgWaitSec : null}
        avgTxSec={series && series.transactionsToday > 0 ? series.avgTransactionSecToday : null}
        longestSec={t.waiting ? t.longestWaitSec : null}
        over={t.overTarget}
        desksOpen={t.desksOpen}
        series={d}
      />
      <ChartRow series={d} times={times} attention={attention} />
      <TrendRow
        series={d}
        times={times}
        byTitle={say('floor.db.byBranch')}
        byRows={byRows}
        vsRows={vsRows}
        desks={overviewDesks(t)}
        desksCaption={say('floor.db.desksAll', { n: t.desks })}
      />
      <Card className="fl-section fl-branches" role="region" aria-labelledby="fl-branches">
        <Card.Header className="fl-card-h">
          <SectionTitle id="fl-branches">{say('floor.branches')}</SectionTitle>
          <Chip size="sm">{named.length}</Chip>
        </Card.Header>
        {named.length === 0 ? (
          <EmptyState>{say('floor.noBranches')}</EmptyState>
        ) : (
          <Table variant="secondary">
            <Table.ScrollContainer>
              <Table.Content aria-label={say('floor.branches')} className="fl-table" onRowAction={(key) => onChoose(String(key))}>
                <Table.Header>
                  <Table.Column isRowHeader>{say('common.branch')}</Table.Column>
                  <Table.Column className="fl-n">{say('floor.db.col.waiting')}</Table.Column>
                  <Table.Column className="fl-n">{say('floor.db.col.beingServed')}</Table.Column>
                  <Table.Column className="fl-n">{say('floor.db.col.served')}</Table.Column>
                  <Table.Column className="fl-n">{say('floor.db.col.openDesks')}</Table.Column>
                  <Table.Column className="fl-n">{say('floor.db.col.avgWait')}</Table.Column>
                  <Table.Column className="fl-n">{say('floor.db.col.longest')}</Table.Column>
                  <Table.Column>{say('floor.db.col.status')}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {paged.rows.map((b) => (
                    <Table.Row key={b.branchId} id={b.branchId} className="fl-row">
                      <Table.Cell>
                        <span className="fl-name" data-closed={!b.openNow || undefined} title={b.code}>
                          <i className="fl-dot" style={{ background: colour(b.branchId) }} />
                          {b.name}
                          {!b.openNow && <Chip size="sm">{say('floor.closed')}</Chip>}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="fl-n">{b.waiting}</Table.Cell>
                      <Table.Cell className="fl-n">{b.serving}</Table.Cell>
                      <Table.Cell className="fl-n">{b.served}</Table.Cell>
                      <Table.Cell className="fl-n">
                        {b.desksOpen} / {b.desks}
                      </Table.Cell>
                      <Table.Cell className="fl-n">{b.waiting ? minWord(b.avgWaitSec, say) : '—'}</Table.Cell>
                      <Table.Cell className="fl-n">{b.waiting ? minWord(b.longestWaitSec, say) : '—'}</Table.Cell>
                      <Table.Cell>
                        <StatusChip kind={b.overTarget > 0 ? 'over' : 'onTrack'} n={b.overTarget} />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        )}
        {named.length > PAGE_SIZES[0] && <Pager {...paged.pager} />}
      </Card>
    </>
  )
}
